import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import * as d3 from 'd3';
import { environment } from '@env/environment';
import { isNil } from 'lodash';
import { EfficiencyAnalysisColorStatus } from '@app/shared/enums';
import { LocalNumberPipe } from '@app/shared/pipes/local-number.pipe';
import { EfficiencyTresholdService } from '@app/efficiency-analysis/services/efficiency-treshold.service';
import { FormatLocaleDefinition } from 'd3';

@Component({
  selector: 'app-line-gauge-chart-tri-color',
  templateUrl: './line-gauge-chart-tri-color.component.html',
  styleUrls: ['./line-gauge-chart-tri-color.component.scss'],
  providers: [LocalNumberPipe],
})
export class LineGaugeChartTriColorComponent implements OnChanges {
  @Input() chartName: string = '';
  @Input() dataRange = { minValue: 0, maxValue: 150 };
  @Input() data: any[] = [];

  consumption: any;
  upperData: any[] = [];

  svgWidth = 470;
  svgHeight = 115;
  xCoordinateLinearScale = 226;
  xCoordinateBar = 46;
  barWidth = 370;
  scaleDifference = 30;
  middleScaleWidth = this.barWidth - this.scaleDifference;
  svg: any;
  lineGaugeSVG: any;
  xScale: any;
  efficientThreshold = 0;
  inefficientThreshold = 0;

  constructor(
    private efficiencyTresholdService: EfficiencyTresholdService,
    private localNumberPipe: LocalNumberPipe,
  ) {
    this.efficiencyTresholdService.treshold.subscribe((x) => {
      this.efficientThreshold = Number(x.efficientTreshold);
      this.inefficientThreshold = Number(x.inefficientTreshold);
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['data'].firstChange && this.data.length > 0) {
      this.renderBar();
      this.renderScale();
      this.renderLabels(this.xScale);
      this.renderMarkers(this.xScale);
      this.renderHoverEffect();
    }
  }

  renderBar() {
    const greenRectLength = this.getRectangleLength(this.efficientThreshold);
    const redRectLength = this.middleScaleWidth - this.getRectangleLength(this.inefficientThreshold);
    const yellowRectLength = this.middleScaleWidth - greenRectLength - redRectLength;

    const green = EfficiencyAnalysisColorStatus.efficient;
    const yellow = EfficiencyAnalysisColorStatus.midEfficient;
    const red = EfficiencyAnalysisColorStatus.inefficient;

    this.svg = d3.select('.line-guage-chart-section').append('svg').attr('class', 'graph').attr('width', this.svgWidth).attr('height', this.svgHeight);
    this.lineGaugeSVG = this.svg.append('g').attr('class', 'g-wrapper').attr('transform', 'translate(0,0)');

    this.lineGaugeSVG
      .append('rect')
      .attr('class', 'rectangle-gauge')
      .attr('x', this.xCoordinateBar)
      .attr('y', 50)
      .attr('ry', 3)
      .attr('width', greenRectLength + this.scaleDifference / 2 + 4)
      .attr('height', 14)
      .style('fill', function () {
        return green;
      });
    this.lineGaugeSVG
      .append('rect')
      .attr('class', 'rectangle-gauge')
      .attr('x', this.xCoordinateBar + greenRectLength + this.scaleDifference / 2 + yellowRectLength - 6)
      .attr('y', 50)
      .attr('ry', 3)
      .attr('width', redRectLength + this.scaleDifference / 2 + 6)
      .attr('height', 14)
      .style('fill', function () {
        return red;
      });
    this.lineGaugeSVG
      .append('rect')
      .attr('class', 'rectangle-gauge')
      .attr('x', this.xCoordinateBar + greenRectLength + this.scaleDifference / 2 + 1)
      .attr('y', 50)
      .attr('width', yellowRectLength - 4)
      .attr('height', 14)
      .style('fill', function () {
        return yellow;
      });
  }

  getRectangleLength(value: any) {
    const xScale = d3.scaleLinear().domain([0, 100]).range([0, this.middleScaleWidth]);
    const result = xScale(value).toFixed(0);
    return parseInt(result, 10);
  }

  renderScale() {
    this.xScale = d3
      .scaleLinear()
      .domain([this.dataRange.minValue, this.dataRange.maxValue])
      .range([-this.middleScaleWidth / 2, this.middleScaleWidth / 2]);

    const step = this.dataRange.maxValue / 10;
    const tickValues = [];
    for (let i = 0; i <= this.dataRange.maxValue; i++) {
      if (i % step === 0) {
        tickValues.push(i);
      }
    }

    const locale = d3.formatLocale({
      thousands: '.',
      grouping: [3],
    } as FormatLocaleDefinition);

    const format = locale.format(',.2d') as (domainValue: any, index: number) => string;

    const xAxis = d3.axisBottom(this.xScale).tickValues(tickValues).tickSize(0).tickFormat(format);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'axis')
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 90)')
      .call(xAxis);
  }

  renderMarkers(xScale: any) {
    const dataWithMaxRange = this.data;
    dataWithMaxRange.forEach((el) => {
      el.maxValue = this.dataRange.maxValue;
    });

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-markers')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(this.data)
      .enter()
      .append('image')
      .attr('class', 'secondary-indicator')
      .attr('transform', function (d: any) {
        if (!isNil(d.value)) {
          return 'translate(' + xScale(d.value) + ', 123)';
        }
      })
      .attr('xlink:href', function (d: any) {
        if (d.indicator === 'lightGrey' && !isNil(d.value)) {
          return './assets/icons/indicator-average.svg';
        }
        if (d.indicator === 'darkGrey' && !isNil(d.value)) {
          return './assets/icons/indicator-standard-guide.svg';
        }
      })
      .attr('width', 28)
      .attr('height', 45)
      .attr('x', -14)
      .attr('y', -90);

    this.lineGaugeSVG
      .append('g')
      .attr('class', 'data-markers')
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(dataWithMaxRange)
      .enter()
      .append('image')
      .attr('class', function (d: any) {
        if (d.indicator === 'companyColor' && !isNil(d.value)) {
          return 'primary-indicator';
        } else {
          return 'secondary-indicator';
        }
      })
      .attr('transform', function (d: any) {
        if (!isNil(d.value) && d.value > d.maxValue) {
          return 'translate(' + xScale(d.maxValue) + ', -80)';
        } else {
          return 'translate(' + xScale(d.value) + ', -80)';
        }
      })
      .attr('xlink:href', function (d: any) {
        if (d.indicator === 'companyColor' && !isNil(d.value)) {
          return `./assets/icons/primary-indicator-${environment.tenant.trim().toLowerCase()}.svg`;
        }
      })
      .attr('width', 33)
      .attr('height', 38)
      .attr('x', function (d: any) {
        if (!isNil(d.value) && d.value > d.maxValue) {
          return 2;
        } else {
          return -16;
        }
      })
      .attr('y', 118.5);
  }

  renderHoverEffect() {
    const className = `.data-labels-${this.chartName}`;

    this.lineGaugeSVG.selectAll('.primary-indicator').on('mouseover', function () {
      d3.selectAll(className).style('visibility', 'visible');
      d3.selectAll(`${className} .label-text`).style('visibility', 'visible');
      d3.selectAll(className).transition().duration(50).style('opacity', 1);
    });

    this.lineGaugeSVG.selectAll('.primary-indicator').on('mouseout', function () {
      d3.selectAll(className).style('visibility', 'hidden');
      d3.selectAll(`${className} .label-text`).style('visibility', 'hidden');
      d3.selectAll(className).transition().duration(50).style('opacity', 0);
    });
  }

  renderLabels(xScale: any) {
    this.upperData.push(this.data.find((value) => value['indicator'] === 'companyColor'));
    this.consumption = this.localNumberPipe.transform(this.upperData[0].value).split(',')[0] + ' ' + this.upperData[0].unit;
    const dataWithMaxRange = this.upperData;
    dataWithMaxRange.forEach((el) => {
      if (el.value < 1) {
        el.localNumberValue = this.localNumberPipe.transform(el.value);
      } else {
        el.localNumberValue = this.localNumberPipe.transform(parseInt(el.value.toFixed(0), 10)).split(',')[0];
        el.value = parseInt(el.value.toFixed(0), 10);
      }
      el.maxValue = this.dataRange.maxValue;
    });

    let space = 0;
    let xRect = 0;
    let wRect = 0;
    let xAllowance = 0;

    this.lineGaugeSVG
      .append('g')
      .attr('class', `data-labels-${this.chartName}`)
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(dataWithMaxRange)
      .enter()
      .append('rect')
      .attr('class', 'label-container')
      .attr('transform', function (x: any) {
        if (!isNil(x.value)) {
          xRect = -54;
          wRect = 110;
          if (x.value > x.maxValue) {
            space = x.maxValue * 0;
            if (x.value.toString().length > 5) {
              space = x.maxValue * 0.02;
              xAllowance = 2;
              xRect = -54;
              wRect = 116;
              if (x.value.toString().length >= 7) {
                space = x.maxValue * 0.03;
                xAllowance = 2;
                xRect = -61;
                wRect = 130;
                if (x.value.toString().length >= 8) {
                  space = x.maxValue * 0.04;
                  xAllowance = 2;
                  xRect = -70;
                  wRect = 143;
                  if (x.value.toString().length >= 10) {
                    space = x.maxValue * 0.05;
                    xAllowance = 2;
                    xRect = -70;
                    wRect = 147;
                  }
                }
              }
            }
            return 'translate(' + xScale(x.maxValue - space) + ', -80)';
          } else {
            return 'translate(' + xScale(x.value) + ', -80)';
          }
        }
      })
      .attr('x', xRect)
      .attr('y', 91)
      .attr('ry', 5)
      .attr('width', wRect)
      .attr('height', 24)
      .style('filter', 'url("#f1")');

    this.lineGaugeSVG
      .append('g')
      .attr('class', `data-labels-${this.chartName}`)
      .attr('y', 220)
      .attr('width', this.middleScaleWidth)
      .attr('transform', 'translate(' + this.xCoordinateLinearScale + ', 0)')
      .selectAll('dot')
      .data(dataWithMaxRange)
      .enter()
      .append('text')
      .attr('class', 'label-text')
      .text(function (e: any) {
        if (!isNil(e.value)) {
          return e.localNumberValue + ' ' + e.unit;
        }
      })
      .attr('transform', function (f: any) {
        if (!isNil(f.value)) {
          if (f.value > f.maxValue) {
            return 'translate(' + xScale(f.maxValue - space) + ', 50)';
          } else {
            return 'translate(' + xScale(f.value) + ', 50)';
          }
        }
      })
      .attr('width', 78)
      .attr('height', 100)
      .attr('x', 2 + xAllowance)
      .attr('y', -22)
      .attr('fill', this.getLabelColor());
  }

  getLabelColor() {
    let color;
    this.data.forEach((el) => {
      if (el.indicator === 'companyColor') {
        color = el.value > el.maxValue ? EfficiencyAnalysisColorStatus.inefficient : '#767676';
      }
    });
    return color;
  }
}
